/**
 * WS-NAV — workspace-scoped sidebar (supersedes the story-050 global tree).
 *
 * Story 050 gave the app ONE sidebar listing every group every user could
 * reach; by mid-2026 that was ~30 leaves across eight disclosure groups, and a
 * pilot saw a tree built for an analyst. This refactor replaces it with a
 * **workspace switcher + the active workspace's branch** — a re-grouping, not
 * a rebuild: every route keeps its URL and its page component, only the chrome
 * changes. See `apps/web/src/app/workspaces/` for the model and
 * `.vibe/DATA_PIPELINES/AERIOS_DEFAULT/workspaces_and_pilot_wizard.md` for the
 * product decisions.
 *
 * What survives from 050 verbatim: the 272 ↔ 64 px collapse and its
 * persistence, the `sidebar.*` token wiring (now in `primitives.tsx`), the
 * brand header, the Documentation row, the notification bell and the user
 * card. What changes: the brand link now points at Home (the workspace
 * overview) rather than at one workspace's list page, and the expandable
 * secondary groups are gone — the workspace IS the group.
 *
 * Below the `md` breakpoint this component does not render at all; the mobile
 * chrome (`MobileNav.tsx`) owns navigation there.
 */
import { useState, type ReactElement } from "react";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Link as MuiLink,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  alpha,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { getSwaggerUrl } from "./swaggerUrl.js";
import {
  AeriosIQLogo,
  AeriosIQMark,
  sidebar as sidebarTokens,
  typography,
} from "@aeriosiq/ui";
import { useAuth, useMe } from "@aeriosiq/auth-client";
import { useUiStore } from "../uiStore.js";
import { HOME_PATH } from "../workspaces/resolve.js";
import { WorkspaceRail } from "./WorkspaceRail.js";
import { WorkspacePanel } from "./WorkspacePanel.js";
import {
  COLLAPSED_WIDTH,
  EXPANDED_WIDTH,
  LEAF_HEIGHT,
  rowStyles,
} from "./primitives.js";
import {
  ChevronLeftIcon,
  ExternalLinkIcon,
  LogOutIcon,
  UserIcon,
} from "@aeriosiq/ui/icons";

export function DocumentationRow(): ReactElement {
  // SB2-W-13 (OQ-D3 lock 2026-05-20) — until an in-product `/documentation`
  // page is built, route to the gateway's SwaggerUI at
  // `${gatewayOrigin}/api/docs`. Opens in a new tab so the user keeps their
  // app session. URL is env-derived via `getSwaggerUrl()`.
  //
  // WS-RAIL — this lost its 32px leading IconCell. Every other row in the
  // panel is text; a single icon-bearing row read as a different KIND of
  // thing, and at 208px it also pushed the external-link glyph off the edge.
  // The trailing glyph stays, because "opens in a new tab" is information the
  // row has to carry (and the accessible name says so too).
  const href = getSwaggerUrl();
  return (
    <Box component="li" sx={{ listStyle: "none", px: 1 }}>
      <MuiLink
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Documentation (opens API docs in a new tab)"
        sx={rowStyles({
          selected: false,
          collapsed: false,
          height: LEAF_HEIGHT,
          pl: 1.5,
        })}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: typography.size["body-md"],
            letterSpacing: typography.tracking["body-md"],
            color: "text.secondary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Documentation
        </Box>
        <ExternalLinkIcon
          fontSize="small"
          aria-hidden
          sx={{ color: "text.secondary", width: 16, height: 16, flexShrink: 0 }}
        />
      </MuiLink>
    </Box>
  );
}

export function BrandHeader({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
}): ReactElement {
  return (
    <Box
      sx={{
        display: "flex",
        // 2026-05-12 (Christian) — anchor the wordmark to a specific inset
        // from the top so the wordmark and the page H1 share a horizontal
        // baseline measured from viewport y=0.
        alignItems: collapsed ? "center" : "flex-start",
        gap: 1.5,
        px: collapsed ? 0 : 2,
        pt: collapsed ? 1.5 : 2.5,
        pb: collapsed ? 1.5 : 2,
        height: collapsed ? 64 : 72,
        flexShrink: 0,
        justifyContent: collapsed ? "center" : "flex-start",
      }}
    >
      {/* WS-NAV — the brand is now the way back to the workspace overview.
          Pre-refactor it pointed at /data-acquisitions, which only made sense
          while every user shared one tree; for a pilot that was a link into
          someone else's workspace. */}
      <MuiLink
        component={RouterLink}
        to={HOME_PATH}
        aria-label="AeriosIQ — go to Home"
        onClick={onNavigate}
        underline="none"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1.5,
          color: "inherit",
          "&:focus-visible": {
            outline: (t) => `2px solid ${t.palette.primary.main}`,
            outlineOffset: 2,
            borderRadius: 1,
          },
        }}
      >
        {collapsed ? (
          <AeriosIQMark height={22} />
        ) : (
          <AeriosIQLogo height={24} />
        )}
      </MuiLink>
    </Box>
  );
}

export function UserCard({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
}): ReactElement {
  const me = useMe();
  const { signOut } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  const displayName = me.data?.displayName ?? "Account";
  const email = me.data?.email ?? "";
  const initials = me.data?.initials ?? "··";
  // Story 052 — the expanded /me payload carries avatarUrl; cast through
  // the narrow contract Me to read it.
  const avatarUrl =
    (me.data as unknown as { avatarUrl?: string | null } | undefined)
      ?.avatarUrl ?? null;
  const appVersion =
    (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "0.0.0";

  return (
    <Box sx={{ px: 1, pb: 1.5 }}>
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          setAnchor(e.currentTarget);
        }}
        aria-label={displayName}
        aria-haspopup="menu"
        aria-expanded={open}
        sx={{
          width: "100%",
          minHeight: 64,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: collapsed ? 0 : 1.5,
          py: 1.5,
          justifyContent: collapsed ? "center" : "flex-start",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
          borderRadius: 1.5,
          color: "text.primary",
          transition: (t) =>
            t.transitions.create("background-color", { duration: 120 }),
          "&:hover": {
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? sidebarTokens.dark.rowHoverBg
                : sidebarTokens.light.rowHoverBg,
          },
          "&:focus-visible": {
            outline: (t) => `2px solid ${t.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        {avatarUrl ? (
          <Box
            component="img"
            src={avatarUrl}
            alt="Profile photo"
            aria-label="Profile photo"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        ) : (
          <Avatar
            alt="Profile photo"
            slotProps={{ img: { "aria-label": "Profile photo" } }}
            sx={{
              width: 40,
              height: 40,
              bgcolor: (t) =>
                alpha(
                  t.palette.text.primary,
                  t.palette.mode === "dark" ? 0.12 : 0.08,
                ),
              color: "text.primary",
              fontSize: 14,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>
        )}
        {collapsed ? null : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              flex: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: typography.size["body-md"],
                letterSpacing: typography.tracking["body-md"],
                fontWeight: typography.weight.medium,
                color: "text.primary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displayName}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: typography.size["caption-lg"],
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {email}
            </Box>
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => {
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <MenuItem
          component={RouterLink}
          to="/account/profile"
          onClick={() => {
            setAnchor(null);
            onNavigate?.();
          }}
        >
          <ListItemIcon>
            <UserIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View profile</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            void signOut();
          }}
        >
          <ListItemIcon>
            <LogOutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
        <Divider />
        <Box
          sx={{
            px: 2,
            py: 1,
            color: "text.tertiary",
            fontSize: typography.size["caption-sm"],
          }}
        >
          <Typography
            component="span"
            variant="caption"
            sx={{ color: "text.tertiary" }}
          >
            v{appVersion}
          </Typography>
        </Box>
      </Menu>
    </Box>
  );
}

export function AppSidebar(): ReactElement {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  return (
    <Box
      component="nav"
      aria-label="Primary navigation"
      sx={(t) => ({
        width,
        flexShrink: 0,
        height: "100vh",
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "row",
        position: "relative",
        overflow: "visible",
        transition: t.transitions.create("width", {
          duration: 200,
          easing: "cubic-bezier(0.2, 0, 0, 1)",
        }),
        // mobile_strategy §2.1 — below `md` the drawer chrome takes over and
        // the main column gets the full viewport width.
        [t.breakpoints.down("md")]: {
          display: "none",
        },
        // The floating collapse pill is hidden by default and revealed on
        // hover anywhere over the nav, or when it takes keyboard focus.
        "&:hover .aero-sidebar-toggle, & .aero-sidebar-toggle:focus-visible": {
          opacity: 1,
          pointerEvents: "auto",
        },
      })}
    >
      {/* WS-RAIL — two columns, two jobs. The rail (64) says which workspace
          and never leaves; the panel (208) says which page and is what
          collapsing hides. 64 + 208 = 272, the width this chrome has always
          been, so nothing on the canvas reflows.

          The brand header, the workspace switcher control, the Documentation
          row and the bell are no longer rendered here — the rail and the panel
          own them now. `BrandHeader`, `UserCard` and `DocumentationRow` are
          still exported from this module because the mobile drawer and the
          rail both consume them. */}
      <WorkspaceRail />
      {collapsed ? null : <WorkspacePanel />}
      {/* Hover-revealed collapse pill — floats on the right-edge seam between
          the sidebar and the canvas, vertically centred. */}
      <IconButton
        className="aero-sidebar-toggle"
        onClick={toggle}
        aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        aria-pressed={collapsed}
        size="small"
        sx={(t) => ({
          position: "absolute",
          top: "50%",
          right: -14,
          width: 28,
          height: 28,
          transform: "translateY(-50%)",
          borderRadius: "50%",
          bgcolor:
            t.palette.mode === "dark" ? "#2A2A2C" : t.palette.background.paper,
          color: "text.secondary",
          border: `1px solid ${t.palette.divider}`,
          boxShadow:
            t.palette.mode === "dark"
              ? "0 1px 2px rgba(0,0,0,0.4)"
              : "0 1px 2px rgba(0,0,0,0.08)",
          opacity: 0,
          pointerEvents: "none",
          transition: t.transitions.create(["opacity", "background-color"], {
            duration: 150,
          }),
          zIndex: 1,
          "&:hover": {
            bgcolor:
              t.palette.mode === "dark" ? "#3A3A3C" : t.palette.action.hover,
            color: "text.primary",
          },
          "&:focus-visible": {
            outline: `2px solid ${t.palette.primary.main}`,
            outlineOffset: 2,
          },
        })}
      >
        <ChevronLeftIcon
          fontSize="small"
          sx={{
            transition: (t) =>
              t.transitions.create("transform", { duration: 200 }),
            transform: collapsed ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </IconButton>
    </Box>
  );
}
